import { Container, FillGradient, Graphics, Text } from "pixi.js";
import { Note } from "../../music/note";
import { noteLabel } from "../../music/notation";
import { getNotationStore } from "../../music/notation-store";
import { PianoLayout } from "../../piano/piano-layout";
import { stackPills } from "../../piano/pill-stack";

/**
 * Le nom des touches indiquées, dans une pastille au-dessus du clavier.
 *
 * Le roll dit où poser les doigts et avec lesquels ; il ne dit jamais le nom
 * de la note. Le panneau le disait, loin des touches. La pastille le pose
 * au-dessus de la touche elle-même, reliée à elle par un trait, dans la
 * couleur de la main — le seul endroit où le regard est déjà.
 *
 * Le nom seul, sans octave : la pastille est posée sur la touche, qui dit
 * déjà où elle est.
 */

const PILL_H_PX = 22;
const PILL_PAD_X_PX = 8;
const NAME_FONT_PX = 13;
/** Espace entre le bas de la pastille et le haut des touches. */
const STEM_PX = 14;
const ROW_GAP_PX = 4;
const PILL_FILL = 0x0e1116;
const PILL_FILL_ALPHA = 0.92;
const EDGE_ALPHA = 0.55;
const STEM_W_PX = 1;
/**
 * Never below 2×. A label rasterised at 1× on a standard screen has hard,
 * stair-stepped edges; drawn at 2× and scaled down, the glyphs are smoothed.
 */
const TEXT_RESOLUTION = Math.max(2, typeof window === "undefined" ? 1 : window.devicePixelRatio);

/**
 * The stem, from the hand's colour at the pill down to dark at the keys: it
 * leaves the pill as part of it and fades before touching the key, instead of
 * a flat line cutting into the keyboard.
 */
const STEM_GRADIENTS = new Map<number, FillGradient>();

function stemGradient(color: number): FillGradient {
  const cached = STEM_GRADIENTS.get(color);

  if (cached !== undefined) {
    return cached;
  }

  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const gradient = new FillGradient({
    type: "linear",
    colorStops: [
      { offset: 0, color: `rgba(${r},${g},${b},0.9)` },
      { offset: 1, color: `rgba(${r},${g},${b},0.08)` },
    ],
  });

  STEM_GRADIENTS.set(color, gradient);

  return gradient;
}

type Pill = {
  readonly bg: Graphics;
  readonly stem: Graphics;
  readonly name: Text;
};

export class PianoHintLabels {
  private readonly layer = new Container();
  /**
   * Every stem under every pill. One layer per pill drew a pill's stem over
   * the pill of the row below, whenever an upper-row stem crossed it.
   */
  private readonly stemLayer = new Container();
  private readonly pillLayer = new Container();
  private readonly pool: Pill[] = [];
  private hints: ReadonlyMap<number, number> = new Map();
  private whiteKeyWidth = 0;
  private destroyed = false;
  private readonly unsubscribeNotation: () => void;

  constructor(stage: Container, private readonly layout: PianoLayout) {
    // Au-dessus du roll, mais rien à cliquer : les gestes du roll passent
    // par le DOM et ne doivent pas buter sur une pastille.
    this.layer.eventMode = "none";
    this.layer.addChild(this.stemLayer, this.pillLayer);
    stage.addChild(this.layer);
    this.unsubscribeNotation = getNotationStore().subscribe(() => this.draw());
  }

  /** Les touches indiquées, et la couleur de chacune. */
  setHints(hints: ReadonlyMap<number, number>): void {
    // Rien à rien : depuis que les notes du rouleau allument le clavier, le
    // signal d'indication passe à chaque note, et il n'apporte aucune
    // pastille — celles-ci ne parlent que des touches qu'une surface
    // d'apprentissage désigne. Redessiner un vide à chaque note serait du
    // travail pur pour une image identique.
    if (hints.size === 0 && this.hints.size === 0) {
      return;
    }

    this.hints = hints;
    this.draw();
  }

  setWhiteKeyWidth(whiteKeyWidth: number): void {
    if (this.whiteKeyWidth === whiteKeyWidth) {
      return;
    }

    this.whiteKeyWidth = whiteKeyWidth;
    this.draw();
  }

  destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.unsubscribeNotation();
    this.layer.destroy({ children: true });
  }

  private draw(): void {
    if (this.destroyed) {
      return;
    }

    const notation = getNotationStore().noteNotation.value;
    const placed: { pill: Pill; center: number; width: number; color: number }[] = [];

    if (this.whiteKeyWidth > 0) {
      const keys = [...this.hints]
        .map(([midi, color]) => ({ key: this.layout.getKeyByMidi(midi), midi, color }))
        .filter((entry) => entry.key !== undefined)
        .map((entry) => ({ ...entry, center: this.keyCenter(entry.key!.color, entry.key!.whiteIndex) }))
        .sort((a, b) => a.center - b.center);

      keys.forEach((entry, i) => {
        const pill = this.acquire(i);
        // A tint over white glyphs, not a fill: changing the fill re-renders
        // the text's texture, the tint is free.
        pill.name.text = noteName(entry.midi, notation);
        pill.name.tint = entry.color;

        const width = PILL_PAD_X_PX * 2 + pill.name.width;

        placed.push({ pill, center: entry.center, width, color: entry.color });
      });
    }

    const rows = stackPills(placed);

    placed.forEach(({ pill, center, width, color }, i) => {
      const left = Math.round(center - width / 2);
      const top = -(STEM_PX + PILL_H_PX + rows[i] * (PILL_H_PX + ROW_GAP_PX));

      pill.bg
        .clear()
        .roundRect(left, top, width, PILL_H_PX, PILL_H_PX / 2)
        .fill({ color: PILL_FILL, alpha: PILL_FILL_ALPHA })
        .stroke({ color, alpha: EDGE_ALPHA, width: 1 });

      pill.stem
        .clear()
        .rect(
          Math.floor(center),
          top + PILL_H_PX,
          STEM_W_PX,
          -(top + PILL_H_PX),
        )
        .fill(stemGradient(color));

      pill.name.x = left + PILL_PAD_X_PX;
      // On the pixel grid: half a pixel off, the glyphs are resampled and
      // read as blurred.
      pill.name.y = Math.round(top + (PILL_H_PX - pill.name.height) / 2);

      pill.bg.visible = true;
      pill.stem.visible = true;
      pill.name.visible = true;
    });

    for (let i = placed.length; i < this.pool.length; i++) {
      const pill = this.pool[i];

      pill.bg.visible = false;
      pill.stem.visible = false;
      pill.name.visible = false;
    }
  }

  /** Même grille arrondie que les touches, pour que le trait tombe au milieu. */
  private keyCenter(color: "white" | "black", whiteIndex: number): number {
    if (color === "black") {
      return Math.round((whiteIndex + 1) * this.whiteKeyWidth);
    }

    const x0 = Math.round(whiteIndex * this.whiteKeyWidth);
    const x1 = Math.round((whiteIndex + 1) * this.whiteKeyWidth);

    return (x0 + x1) / 2;
  }

  private acquire(index: number): Pill {
    const existing = this.pool[index];

    if (existing !== undefined) {
      return existing;
    }

    const stem = new Graphics();
    const bg = new Graphics();
    // `Text` and not `BitmapText`, like every other label on the roll: it is
    // rasterised at the renderer's resolution, where a bitmap font was drawn
    // once at 1× and came out blurred on a dense screen.
    const name = new Text({
      text: "",
      resolution: TEXT_RESOLUTION,
      style: {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: NAME_FONT_PX,
        fontWeight: "500",
        fill: 0xffffff,
      },
    });

    // The keyboard is panned by a fractional offset, so the text would land
    // between pixels and be resampled. Snapped at render time instead.
    name.roundPixels = true;
    this.stemLayer.addChild(stem);
    this.pillLayer.addChild(bg, name);

    const pill = { bg, stem, name };
    this.pool.push(pill);

    return pill;
  }
}

/** `66` → `Fa♯`, sans octave, dans la notation du joueur. */
function noteName(
  midi: number,
  notation: ReturnType<typeof getNotationStore>["noteNotation"]["value"],
): string {
  return noteLabel(Note.fromMidi(midi).toName().replace(/-?\d+$/, ""), notation);
}
